export interface Category {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  image_url: string | null;
  is_active: boolean;
  /** Active sellable items (standalone products + variants, once per SKU), subcategories included. */
  product_count: number;
  /** Detail only: active products assigned directly to this category — what blocks deleting it. */
  direct_product_count?: number;
  /** FR-BILL-053: category return window in days; null = tenant default, 0 = non-returnable. */
  return_window_days?: number | null;
  /** FR-BILL-053: category approval threshold in minor units; null = tenant default, 0 = off. */
  return_approval_threshold?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CategoryWithChildren extends Category {
  children: CategoryWithChildren[];
}

export interface CategoryListResponse {
  data: Category[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

/** POST /categories — no is_active: a new category is always active (FORMS.md §5.7). */
export interface CreateCategoryPayload {
  name: string;
  parent_id?: string | null;
  description?: string | null;
  image_url?: string | null;
}

export interface UpdateCategoryPayload {
  name?: string;
  parent_id?: string | null;
  description?: string | null;
  image_url?: string | null;
  is_active?: boolean;
  return_window_days?: number | null;
  return_approval_threshold?: number | null;
}

// ─── Validation constants (FORMS.md §5.7) ───────────────────────────────────

export const CATEGORY_NAME_MIN = 2;
export const CATEGORY_NAME_MAX = 200;
export const CATEGORY_DESC_MAX = 500;
export const CATEGORY_IMAGE_MAX_SIZE = 2 * 1024 * 1024; // 2MB
export const CATEGORY_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';

/** Strip HTML tags from user input (XSS prevention) */
export function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, '');
}

/** Validate category name per FORMS.md §5.7 */
export function validateCategoryName(name: string): string | null {
  const trimmed = stripHtml(name).trim();
  if (!trimmed) return 'Category name is required';
  if (trimmed.length < CATEGORY_NAME_MIN)
    return `Category name must be at least ${CATEGORY_NAME_MIN} characters`;
  if (trimmed.length > CATEGORY_NAME_MAX)
    return `Category name must be at most ${CATEGORY_NAME_MAX} characters`;
  return null;
}

// ─── Tree builder ────────────────────────────────────────────────────────────

export function buildCategoryTree(categories: Category[]): CategoryWithChildren[] {
  const map = new Map<string, CategoryWithChildren>();
  categories.forEach((c) => map.set(c.id, { ...c, children: [] }));
  const roots: CategoryWithChildren[] = [];
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots.sort((a, b) => a.name.localeCompare(b.name));
}
